"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheSonicJourney() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const principles = [{
    number: "01",
    name: "SONIC PURITY",
    description: "Stripping back the noise to find the raw, celestial frequency of pop."
  }, {
    number: "02",
    name: "MIDNIGHT ENERGY",
    description: "Music designed for the hours when the world goes quiet and the stars align."
  }, {
    number: "03",
    name: "VISUAL ECLIPSE",
    description: "A total sensory experience where light and shadow dance in perfect sync."
  }];
  return <section data-section-id="2653" id="the-sonic-journey" className="bg-background text-foreground min-h-[90vh] w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[90vh]">
        {}
        <div ref={imageRef} className={`relative bg-black overflow-hidden min-h-[60vh] lg:min-h-[90vh] transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_concert_magenta_flare.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_concert_magenta_flare.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693015/img/eclipse_concert_magenta_flare.png-wvc-sizes" alt="The Eclipse performing under intense magenta stage lights" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 mix-blend-screen" />
          {}
          <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
        }} />
          {}
          <div className="absolute inset-0 bg-gradient-to-tr from-background/80 via-transparent to-transparent" />

          {}
          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 z-10">
            <p className="font-mono text-xs md:text-sm tracking-widest" style={{
            color: "var(--primary)"
          }}>
              LAT: 34.0522 / LON: 118.2437 / 02:00 AM
            </p>
          </div>
        </div>

        {}
        <div ref={textRef} className={`relative px-6 py-16 sm:px-10 sm:py-20 md:px-16 md:py-24 lg:px-[120px] lg:py-[120px] flex flex-col justify-center transition-all duration-1000 ease-out ${textVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        backgroundColor: "var(--background)",
        color: "var(--foreground)"
      }}>
          {}
          <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-8 md:mb-10" style={{
          color: "var(--muted-foreground)"
        }}>
            The Sonic Journey
          </p>

          {}
          <h2 className="font-default font-bold uppercase mb-10 md:mb-12" style={{
          fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
          lineHeight: "0.9",
          letterSpacing: "-0.04em",
          color: "var(--foreground)"
        }}>
            BEYOND
            <br />
            THE DARK
            <br />
            <span style={{
            color: "var(--primary)"
          }}>LIGHT</span> IS
            <br />
            BORN.
          </h2>

          {}
          <div className="font-default text-lg md:text-xl leading-relaxed space-y-5 mb-10 md:mb-12 max-w-xl" style={{
          color: "var(--muted-foreground)"
        }}>
            <p>
              The Eclipse emerged from the neon-soaked streets of the underground pop scene, 
              driven by a desire to create music that feels like a celestial event. 
              No compromises, no radio-friendly filters—just pure, atmospheric soundscapes 
              that bridge the gap between high-fashion noir and infectious pop energy.
            </p>
            <p>
              Every track is a transmission from the void. We invite you to step into the 
              shadows where the most vibrant colors are found. This isn't just a performance; 
              it's a total immersion into a world where the sun never quite rises.
            </p>
          </div>

          {}
          <div className="h-[1px] w-20 mb-10 md:mb-12" style={{
          backgroundColor: "var(--primary)"
        }} />

          {}
          <div className="space-y-8 md:space-y-10 mb-12 md:mb-14">
            {principles.map((principle, i) => <div key={principle.number} data-index={i} className="flex gap-5 md:gap-7 items-start">
                <span className="font-mono text-sm md:text-base font-medium pt-1 shrink-0" style={{
              color: "var(--primary)"
            }}>
                  {principle.number}
                </span>
                <div className="flex-1">
                  <h3 className="font-default font-bold uppercase text-xl md:text-2xl mb-2 tracking-tight" style={{
                color: "var(--foreground)"
              }}>
                    {principle.name}
                  </h3>
                  <p className="font-default text-base md:text-lg leading-snug" style={{
                color: "var(--muted-foreground)"
              }}>
                    {principle.description}
                  </p>
                </div>
              </div>)}
          </div>

          {}
          <p className="font-default italic text-xl md:text-2xl" style={{
          color: "var(--foreground)"
        }}>
            The Eclipse, est. 2024.
          </p>
        </div>
      </div>
    </section>;
}